import { InternalAccessor } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { BillState } from '@features/bills/enums/billState'
import { BillType } from '@features/bills/enums/billType'
import { Bill } from '@features/bills/types/bill'
import { getBillStatus } from '@features/bills/utils/getBillStatus'
import { Contact } from '@features/contacts/types/contact'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { AccountingBasis } from '@modules-deprecated/app/organization/types/accountingBasis'
import { BalanceModifier } from '@modules-deprecated/inbox/types'
import { getPaymentIdFromBalance } from '@modules-deprecated/inbox/utils'

import { getOrganizationViewUrl } from '../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { getContactName } from '../../contacts/utils/getContactName'
import { BillsTableAccessor } from '../routes/BillsList/legacy/enums/billsTableAccessor'
import { BillsTableRow } from '../routes/BillsList/legacy/types/billsTableRow'
import { getBillAmountToDisplayValue } from '../utils/getBillAmountToDisplayValue'
import { getBillsTableStaticActions } from '../utils/getBillsTableStaticActions'
import { getLatestIntegrationStatus } from '../utils/getLatestIntegrationStatus'
import { isBillSyncRetryable } from '../utils/isBillSyncRetryable'

export const useBillsTableData = (bills: Bill[], contacts: Contact[]): BillsTableRow[] => {
  const { t } = useTranslation()
  const location = useLocation()
  const { organization } = useUserOrganization()
  const organizationUrl = organization?.url

  const getBillUrl = useCallback(
    (routeFunction: (id: string) => string, id: string) =>
      `${getOrganizationViewUrl(organizationUrl, routeFunction(id))}${location.search}`,
    [organizationUrl, location.search],
  )

  return useMemo(
    () =>
      bills.map((bill) => {
        const {
          accountingBasis,
          amount,
          attachments,
          balance,
          balanceModifiers,
          currencyId,
          contactId,
          dueDate,
          entryDate,
          grossAmount,
          id,
          integrationStatus,
          isPaid,
          lineDescription,
          state,
          suppliersInvoiceNo,
          voucherNo,
          tax,
          type,
        } = bill
        const isCreditNote = type === BillType.CreditNote
        const canRegisterPayment = !isPaid && state === BillState.Approved
        const isDraft = state === BillState.Draft
        const isCashBasedAndPaid = accountingBasis === AccountingBasis.Cash && isPaid
        const safeModifiers: BalanceModifier[] = balanceModifiers.filter(
          (mod): mod is BalanceModifier => mod.isVoided !== null,
        )
        const paymentId = getPaymentIdFromBalance(safeModifiers, id)

        const viewBillUrl = getBillUrl(reactRoute.organization.bills.getViewBillRoute, id)
        const editBillUrl = getBillUrl(reactRoute.organization.bills.getEditBillRoute, id)
        const duplicateBillUrl = getBillUrl(reactRoute.organization.bills.getDuplicateRoute, id)
        const createCreditNoteUrl = getBillUrl(reactRoute.organization.bills.getCreateCreditNoteRoute, id)

        const cegidIntegrationStatus = getLatestIntegrationStatus(integrationStatus)
        const isSyncRetryable = isBillSyncRetryable({ integrationStatus })

        const staticActionsItems = getBillsTableStaticActions({
          editBillUrl,
          duplicateBillUrl,
          createCreditNoteUrl,
          canRegisterPayment,
          isCashBasedAndPaid,
          isDraft,
          hasIntegrationSync: Boolean(cegidIntegrationStatus),
          isSyncRetryable,
          t,
        })

        const billPath = state === BillState.Draft ? editBillUrl : viewBillUrl

        return {
          [InternalAccessor.UrlInternal]: billPath,
          [BillsTableAccessor.Attachment]: !!attachments.length,
          [BillsTableAccessor.Balance]:
            balance && currencyId
              ? getBillAmountToDisplayValue({ amount: balance, currency: currencyId, isCreditNote: false })
              : undefined,
          [BillsTableAccessor.Date]: entryDate,
          [BillsTableAccessor.Description]: lineDescription
            ? {
                isCreditNote,
                value: lineDescription,
              }
            : undefined,
          [BillsTableAccessor.DueDate]: !isPaid && dueDate ? dueDate : null,
          [BillsTableAccessor.ExcludedVat]:
            amount !== null && currencyId
              ? getBillAmountToDisplayValue({ amount, currency: currencyId, isCreditNote })
              : undefined,
          [BillsTableAccessor.IncludedVat]:
            grossAmount !== null && currencyId
              ? getBillAmountToDisplayValue({ amount: grossAmount, currency: currencyId, isCreditNote })
              : undefined,
          [BillsTableAccessor.IntegrationStatus]: cegidIntegrationStatus,
          [BillsTableAccessor.PaymentId]: paymentId,
          [BillsTableAccessor.RefNo]: suppliersInvoiceNo,
          [BillsTableAccessor.Status]: getBillStatus({
            amount,
            balance,
            dueDate: dueDate ? new Date(dueDate) : undefined,
            isPaid,
            state,
            tax,
          }),
          [BillsTableAccessor.Vendor]: contactId ? getContactName(contacts, contactId) : undefined,
          [BillsTableAccessor.VoucherNo]: voucherNo,
          id,
          [InternalAccessor.HoverActions]: canRegisterPayment,
          [InternalAccessor.StaticActions]: true,
          bill,
          staticActionsItems,
        }
      }),
    [bills, contacts, getBillUrl, t],
  )
}
